import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  InvestmentAnswer,
  InvestmentQuestion,
  InvestmentQuestionOption,
  InvestmentSplitWithAnswers,
} from './utils';
import { Program } from '../../Reports/NewReportDialog';
import { Task } from '../api';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import {
  allocationQuestionText,
  firstInvestmentQuestionId,
  investmentQuestions,
} from './questions';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { SelectorOptions } from 'shared/design-system/components/Selector';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import AllocationAllDone from './AllocationAllDone';
import { RequestState } from 'shared/auth/hooks/useRequestState';
import { AllocationRequestResponse } from './api';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import { AllocationReports } from './allocationLocalStorage';

const getInitialAnswer = (
  isFinished: boolean,
  investment: Required<InvestmentSplitWithAnswers>,
  currentAnswerIndex: number,
) => {
  return isFinished
    ? undefined
    : investment.answers.length === 0
    ? undefined
    : investment.answers[currentAnswerIndex];
};

const getInitialQuestion = (
  isFinished: boolean,
  answer: InvestmentAnswer | undefined,
) => {
  return isFinished
    ? undefined
    : answer
    ? investmentQuestions[answer.question.questionId]
    : investmentQuestions[firstInvestmentQuestionId];
};

interface AllocationQuestionBase {
  investment: Required<InvestmentSplitWithAnswers>;
}

export interface AllocationQuestionProps {
  task: Task;
  totalAmount: number;
  finishedIndexes: Set<number>;
  totalFinishedInvestments: number;
  allPrograms: Program[];
  allocatingRequestState: RequestState<AllocationRequestResponse>;
  reports: AllocationReports;
  handleBack: () => unknown;
  investmentFinalSplitIndex: number;
  setFinishedIndex: React.Dispatch<React.SetStateAction<Set<number>>>;
  selectedProgram: Program | undefined;
  investmentSplit: Required<InvestmentSplitWithAnswers>[];
  isImpactSnapshot?: boolean;
  handleAllocationSave: (
    investment: Required<InvestmentSplitWithAnswers>[] | undefined,
  ) => unknown;
  setInvestmentFinalSplit: React.Dispatch<
    React.SetStateAction<Required<InvestmentSplitWithAnswers>[] | undefined>
  >;
}

type InvestmentAllocation = AllocationQuestionProps & AllocationQuestionBase;

const StyledDiv = styled.div`
  margin-top: ${SPACERS.small};
  .allocation-question-text {
    margin-bottom: ${SPACERS.small};
  }
  .allocation-question-buttons {
    margin-top: ${SPACERS.small};
    display: flex;
    gap: ${SPACERS.small};
    align-items: center;
    justify-content: center;
  }
`;

const AllocationQuestion: React.FC<InvestmentAllocation> = ({
  reports,
  investment,
  investmentSplit,
  finishedIndexes,
  allocatingRequestState,
  totalFinishedInvestments,
  investmentFinalSplitIndex,
  isImpactSnapshot,
  handleAllocationSave,
  setInvestmentFinalSplit,
  setFinishedIndex,
}) => {
  const isFinished = finishedIndexes.has(investmentFinalSplitIndex);
  const lastAnswerIndex = investment.answers.length - 1;
  const [currentAnswerIndex, setCurrentAnswerIndex] = useState<number>(
    lastAnswerIndex < 0 ? 0 : lastAnswerIndex,
  );
  const [answer, setAnswer] = useState<InvestmentAnswer | undefined>(
    getInitialAnswer(isFinished, investment, currentAnswerIndex),
  );

  const [question, setQuestion] = useState<InvestmentQuestion | undefined>(
    isImpactSnapshot ? undefined : getInitialQuestion(isFinished, answer),
  );

  useEffect(() => {
    if (!isImpactSnapshot || isFinished) return;
    setQuestion(undefined);
    const split = investmentSplit[investmentFinalSplitIndex];
    if (split) handleAllocationSave([split]);
    setFinishedIndex(s => {
      const newState = new Set(s);
      newState.add(investmentFinalSplitIndex);
      return newState;
    });
  }, [investmentFinalSplitIndex]);

  useEffect(() => {
    if (isImpactSnapshot) return;
    const index = lastAnswerIndex < 0 ? 0 : lastAnswerIndex;
    setCurrentAnswerIndex(index);
    setAnswer(getInitialAnswer(isFinished, investment, index));
    setQuestion(getInitialQuestion(isFinished, answer));
  }, [investmentFinalSplitIndex]);

  useEffect(() => {
    setAnswer(investment.answers[currentAnswerIndex]);
  }, [investmentSplit]);

  const handleChange = useCallback(
    (v: InvestmentQuestionOption, i: number) => {
      setInvestmentFinalSplit(s => {
        if (!s) return s;
        const newState = [...s];
        newState[investmentFinalSplitIndex].answers[currentAnswerIndex] = {
          answer: v.optionText,
          question: question as InvestmentQuestion,
          optionIndex: i,
        };
        return newState;
      });
    },
    [question, investmentFinalSplitIndex, setInvestmentFinalSplit],
  );

  const handleNext = useCallback(() => {
    if (!answer) return;
    const nextQuestionId =
      answer.question.options[answer.optionIndex].nextQuestionId;
    if (!nextQuestionId) {
      setQuestion(undefined);
      handleAllocationSave([investmentSplit[investmentFinalSplitIndex]]);
      setFinishedIndex(s => {
        const newState = new Set(s);
        newState.add(investmentFinalSplitIndex);
        return newState;
      });
      return;
    }
    setCurrentAnswerIndex(s => s + 1);
    const nextQuestion = investmentQuestions[nextQuestionId];
    setQuestion(nextQuestion);
    const nextAnswer = investmentSplit[investmentFinalSplitIndex].answers.find(
      x => x.question.questionId === nextQuestion?.questionId,
    );
    setAnswer(nextAnswer);
  }, [
    answer,
    investmentSplit,
    investmentFinalSplitIndex,
    handleAllocationSave,
    setCurrentAnswerIndex,
    setQuestion,
    setAnswer,
    setFinishedIndex,
  ]);

  const handleBack = useCallback(() => {
    const lastAnswerIndex = currentAnswerIndex - 1;
    const lastAnswer =
      investmentSplit[investmentFinalSplitIndex].answers[lastAnswerIndex];
    setAnswer(lastAnswer);
    setQuestion(lastAnswer.question);
    setCurrentAnswerIndex(lastAnswerIndex);
    // setFinishedIndex(s => {
    //   const newState = [...s];
    //   const index = newState.findIndex(x => x === investmentIndex);
    //   if (index === -1) return s;
    //   newState.splice(index, 1);
    //   return newState;
    // });
  }, [
    investmentSplit,
    investmentFinalSplitIndex,
    currentAnswerIndex,
    setCurrentAnswerIndex,
    setAnswer,
    setQuestion,
  ]);

  if (!question) {
    if (allocatingRequestState.type === 'REQUEST_ERROR') {
      const handleRetry = isImpactSnapshot
        ? () => {
            const split = investmentSplit[investmentFinalSplitIndex];
            if (split) handleAllocationSave([split]);
          }
        : handleNext;
      return (
        <>
          <TrueImpactText color="critical">
            Unable to allocate this investment. Please try again!
          </TrueImpactText>
          <TrueImpactButton onClick={handleRetry} className="try-again-button">
            Try Again!
          </TrueImpactButton>
        </>
      );
    }
    const reportId = reports[investment.program.name]?.[Number(investment.year)];
    if (!reportId) {
      return <TrueImpactLoading />;
    }
    return (
      <AllocationAllDone
        reportId={reportId}
        investment={investment}
        totalInvestments={investmentSplit.length}
        allocatingRequestState={allocatingRequestState}
        totalFinishedInvestments={totalFinishedInvestments}
        isImpactSnapshot={isImpactSnapshot}
      />
    );
  }

  const questionText = allocationQuestionText[question.questionId];

  return (
    <StyledDiv>
      <TrueImpactText
        variant="body-large-bold"
        className="allocation-question-text"
      >
        {questionText?.questionText || question.questionText}
      </TrueImpactText>
      <SelectorOptions
        variant="radio"
        padding={SPACERS.tiny}
        options={question.options}
        getLabel={(p, i) => questionText?.options[i] || p.optionText}
        getSelected={val => (answer ? answer.answer === val.optionText : false)}
        onClick={handleChange}
      />
      <div className="allocation-question-buttons">
        <TrueImpactButton
          variant="secondary-large"
          disabled={currentAnswerIndex === 0}
          onClick={handleBack}
        >
          Back
        </TrueImpactButton>
        <TrueImpactButton
          variant="primary-large"
          onClick={handleNext}
          disabled={!answer?.answer}
        >
          Next
        </TrueImpactButton>
      </div>
    </StyledDiv>
  );
};

export default AllocationQuestion;
